<template>
    <benefits-modal name="statement" ref="modal" @open="onOpen" @close="unbindWheel">
        <section
            class="statement"
            :class="{ 'is-dragging': dragging || wheeling }"
            :style="{ transform: 'translateY(' + offset + 'px)' }"
        >
            <button
                type="button"
                class="statement-handle"
                :aria-label="t(expanded ? 'dashboard-statementCollapse' : 'dashboard-statementExpand')"
                @click="toggle"
                @mousedown="startDrag"
                @touchstart="startDrag"
            >
                <span class="statement-grip"></span>
            </button>

            <h1 class="statement-title" v-lang="'dashboard-statementTitle'"></h1>

            <benefit-tabs />

            <loader v-if="loading" />

            <template v-else>
                <p class="statement-balance">
                    <span v-lang="'dashboard-statementBalance'"></span>
                    <strong>{{ balance }}</strong>
                </p>

                <p class="statement-empty" v-if="!groups.length" v-lang="'dashboard-statementEmpty'"></p>

                <div class="statement-groups" v-else>
                    <section class="statement-group" v-for="group in groups" :key="group.key">
                        <h2 class="statement-month">{{ group.label }}</h2>

                        <div class="statement-entries">
                            <template v-for="entry in group.items">
                                <span class="statement-connector" :key="entry.id + '-line'"></span>

                                <article class="statement-entry" :key="entry.id">
                                    <p class="statement-when">{{ dateLine(entry.date) }}</p>

                                    <p
                                        class="statement-headline"
                                        :class="entry.type === 'credit' ? 'is-credit' : 'is-debit'"
                                    >
                                        {{ headline(entry) }}
                                    </p>

                                    <span class="statement-context" v-if="context(entry)">
                                        {{ context(entry) }}
                                    </span>
                                </article>
                            </template>
                        </div>
                    </section>
                </div>
            </template>
        </section>
    </benefits-modal>
</template>

<script>
import Api from "../../api";
import config from "../../config";
import loader from "../../shared/loader";
import BenefitsModal from "../../shared/modal-shell";
import BenefitTabs from "../../shared/program-tabs";

const EXPANDED_OFFSET = -73;
const LOCALES = { ptBr: "pt-BR", en: "en-US", es: "es-ES" };

export default {
    name: "StatementModal",
    components: { loader, BenefitsModal, BenefitTabs },
    data() {
        return {
            transactions: [],
            balance: 0,
            loading: true,
            offset: 0,
            dragging: false,
            wheeling: false,
            dragged: false,
            dragStartY: 0,
            dragStartOffset: 0,
        };
    },
    created() {
        this.panelEl = null;
        this.wheelTimer = null;
    },
    beforeDestroy() {
        this.unbindDrag();
        this.unbindWheel();
    },
    computed: {
        locale() {
            return LOCALES[this.$root.lang] || LOCALES.ptBr;
        },
        expanded() {
            return this.offset < EXPANDED_OFFSET / 2;
        },
        groups() {
            const buckets = [];
            const seen = {};

            this.transactions.forEach((entry) => {
                const date = this.parseDate(entry.date);
                if (!date) {
                    return;
                }

                const key = date.getFullYear() + "-" + date.getMonth();

                if (seen[key] === undefined) {
                    seen[key] = buckets.length;
                    buckets.push({ key: key, label: this.monthLabel(date), items: [] });
                }

                buckets[seen[key]].items.push(entry);
            });

            return buckets;
        },
    },
    methods: {
        onOpen() {
            this.offset = 0;
            this.loadStatement();
            this.$nextTick(this.bindWheel);
        },
        loadStatement() {
            if (config.isPreview) {
                this.transactions = this.$store.state.dashboard.transactions;
                this.balance = this.$store.state.dashboard.balance;
                this.loading = false;

                return;
            }

            this.loading = true;

            Api.get("client/statement")
                .then((response) => {
                    const body = response.body || {};

                    this.transactions = body.transactions || [];
                    this.balance = body.balance || 0;
                })
                .catch(() => {
                    this.transactions = [];
                })
                .then(() => {
                    this.loading = false;
                });
        },

        parseDate(raw) {
            const match = String(raw || "").match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
            if (!match) {
                return null;
            }

            return new Date(+match[1], +match[2] - 1, +match[3], +match[4], +match[5]);
        },
        capitalize(value) {
            return value.charAt(0).toUpperCase() + value.slice(1);
        },
        monthLabel(date) {
            const month = date.toLocaleDateString(this.locale, { month: "long" });

            return this.capitalize(month) + "/" + date.getFullYear();
        },
        pad(value) {
            return value < 10 ? "0" + value : String(value);
        },
        formatDateTime(date) {
            return {
                date: this.pad(date.getDate()) + "/" + this.pad(date.getMonth() + 1) + "/" + date.getFullYear(),
                time: this.pad(date.getHours()) + ":" + this.pad(date.getMinutes()),
                weekday: this.capitalize(date.toLocaleDateString(this.locale, { weekday: "long" })),
            };
        },
        dateLine(raw) {
            const date = this.parseDate(raw);

            return date ? this.t("dashboard-statementDateLine", this.formatDateTime(date)) : "";
        },
        headline(entry) {
            const points = Math.abs(entry.amount);
            const one = points === 1;

            if (entry.type === "credit") {
                return one
                    ? this.t("dashboard-statementReceivedOne")
                    : this.t("dashboard-statementReceived", { points: points });
            }

            return one
                ? this.t("dashboard-statementUsedOne")
                : this.t("dashboard-statementUsed", { points: points });
        },
        context(entry) {
            if (entry.rewardName) {
                return this.t("dashboard-statementReward", { name: entry.rewardName });
            }

            if (entry.orderDate) {
                const date = this.parseDate(entry.orderDate);

                if (date) {
                    return this.t("dashboard-statementPurchase", {
                        date: this.t("dashboard-statementDateLine", this.formatDateTime(date)),
                    });
                }
            }

            if (entry.userName) {
                return this.t("dashboard-statementAddedBy", { name: entry.userName });
            }

            return entry.type === "credit" ? this.t("dashboard-statementAdded") : "";
        },

        toggle() {
            if (this.dragged) {
                this.dragged = false;
                return;
            }

            this.offset = this.expanded ? 0 : EXPANDED_OFFSET;
        },
        startDrag(event) {
            this.dragging = true;
            this.dragged = false;
            this.dragStartY = this.pointerY(event);
            this.dragStartOffset = this.offset;

            document.addEventListener("mousemove", this.onDrag);
            document.addEventListener("mouseup", this.endDrag);
            document.addEventListener("touchmove", this.onDrag, { passive: false });
            document.addEventListener("touchend", this.endDrag);
        },
        onDrag(event) {
            if (!this.dragging) {
                return;
            }

            if (event.cancelable) {
                event.preventDefault();
            }

            const delta = this.pointerY(event) - this.dragStartY;
            if (Math.abs(delta) > 3) {
                this.dragged = true;
            }

            this.offset = Math.min(0, Math.max(EXPANDED_OFFSET, this.dragStartOffset + delta));
        },
        endDrag() {
            if (!this.dragging) {
                return;
            }

            this.dragging = false;
            this.offset = this.offset < EXPANDED_OFFSET / 2 ? EXPANDED_OFFSET : 0;
            this.unbindDrag();
        },
        unbindDrag() {
            document.removeEventListener("mousemove", this.onDrag);
            document.removeEventListener("mouseup", this.endDrag);
            document.removeEventListener("touchmove", this.onDrag);
            document.removeEventListener("touchend", this.endDrag);
        },
        pointerY(event) {
            return event.touches && event.touches.length ? event.touches[0].clientY : event.clientY;
        },

        bindWheel() {
            this.unbindWheel();

            const panel = this.$refs.modal && this.$refs.modal.scrollElement();
            if (!panel) {
                return;
            }

            this.panelEl = panel;
            panel.addEventListener("wheel", this.onWheel, { passive: false });
        },
        unbindWheel() {
            if (this.panelEl) {
                this.panelEl.removeEventListener("wheel", this.onWheel);
                this.panelEl = null;
            }

            clearTimeout(this.wheelTimer);
            this.wheeling = false;
        },
        onWheel(event) {
            const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
            const goingDown = delta > 0;
            const atTop = !this.panelEl || this.panelEl.scrollTop <= 0;

            const canOpen = goingDown && this.offset > EXPANDED_OFFSET;
            const canClose = !goingDown && atTop && this.offset < 0;

            if (!canOpen && !canClose) {
                return;
            }

            event.preventDefault();
            this.offset = Math.min(0, Math.max(EXPANDED_OFFSET, this.offset - delta));
            this.holdWheel();
        },
        holdWheel() {
            this.wheeling = true;
            clearTimeout(this.wheelTimer);

            this.wheelTimer = setTimeout(() => {
                this.wheeling = false;
                this.offset = this.expanded ? EXPANDED_OFFSET : 0;
            }, 120);
        },
    },
};
</script>

<style lang="scss">
.statement {
  position: relative;
  z-index: 2;
  flex: 1 1 auto;
  width: calc(100% - 1rem);
  max-width: 30rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 1rem 2.5rem;
  background: #fcfcfd;
  border: 1px dashed #d2d6db;
  border-radius: 1rem;
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);

  &.is-dragging {
    transition: none;
  }

  @media (min-width: 48rem) {
    max-width: 37.5rem;
  }
}

.statement-handle {
  align-self: center;
  padding: 0.5rem 1.5rem;
  border: none;
  background: transparent;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
}

.statement-grip {
  display: block;
  width: 2rem;
  height: 0.25rem;
  border-radius: 5rem;
  background: #d9d9d9;
}

.statement-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.3125;
  color: #384250;
  text-align: center;
}

.statement-balance {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  font-size: clamp(1.75rem, 9vw, 3rem);
  font-weight: 400;
  line-height: 1.2;
  color: #111927;
  text-align: center;
}

.statement-balance strong {
  font-weight: 400;
  color: #17b26a;
}

.statement-groups {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.statement-group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.statement-month {
  margin: 0;
  padding: 0.5rem;
  border-radius: 0.5rem;
  background: #f3f4f6;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.3125;
  color: #384250;
  text-align: center;
}

.statement-entries {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.statement-connector {
  width: 0.125rem;
  height: 2rem;
  background: #f3f4f6;
}

.statement-entry {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #f9fafb;
}

.statement-when {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.1667;
  color: #6c727e;
}

.statement-headline {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2857;
}

.statement-headline.is-credit {
  color: #17b26a;
}

.statement-headline.is-debit {
  color: #384250;
}

.statement-context {
  padding: 0.375rem;
  border-radius: 0.25rem;
  background: #e5e7eb;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.1667;
  color: #384250;
  overflow-wrap: anywhere;
}

.statement-empty {
  margin: 0;
  padding: 1.5rem 0;
  font-size: 0.875rem;
  color: #6c737f;
  text-align: center;
}
</style>
